/**
 * @file useColorScheme.js
 * @description A composable for managing the dashboard color scheme (light/dark/auto).
 * The whole feature is gated behind the `dark_mode` account feature flag, so this also
 * owns forcing the UI back to light when the flag is off.
 */

import { computed, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { setColorTheme, forceLightTheme } from 'dashboard/helper/themeHelper';

export const COLOR_SCHEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  AUTO: 'auto',
};

const darkModeMediaQuery = () =>
  window.matchMedia('(prefers-color-scheme: dark)');

/**
 * Shared across every consumer of the composable so we never register the
 * `prefers-color-scheme` listener more than once.
 * @type {MediaQueryList|null}
 */
let mediaQuery = null;

const listenToOSThemeChanges = () => {
  if (mediaQuery) return;
  mediaQuery = darkModeMediaQuery();
  mediaQuery.onchange = e => setColorTheme(e.matches);
};

const stopListeningToOSThemeChanges = () => {
  if (!mediaQuery) return;
  mediaQuery.onchange = null;
  mediaQuery = null;
};

/**
 * Color scheme management composable
 *
 * @returns {Object} Color scheme utilities and state
 * @property {import('vue').ComputedRef<boolean>} isDarkModeEnabled - Whether the `dark_mode` feature flag is on
 * @property {Function} setAppearance - Persist a color scheme and apply it to the document
 */
export const useColorScheme = () => {
  const accountId = useMapGetter('getCurrentAccountId');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  /**
   * The account is fetched asynchronously, so this flips from false to true
   * once `accounts/get` resolves. The watcher below applies the theme then.
   * @type {import('vue').ComputedRef<boolean>}
   */
  const isDarkModeEnabled = computed(() =>
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.DARK_MODE)
  );

  /**
   * Apply the stored color scheme, or force light when the flag is off.
   * @returns {void}
   */
  const applyColorScheme = () => {
    if (!isDarkModeEnabled.value) {
      forceLightTheme();
      return;
    }
    setColorTheme(darkModeMediaQuery().matches);
  };

  /**
   * Persist the selected color scheme and apply it to the document
   * @param {string} theme - One of COLOR_SCHEMES
   * @returns {void}
   */
  const setAppearance = theme => {
    LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, theme);
    applyColorScheme();
  };

  watch(
    isDarkModeEnabled,
    enabled => {
      if (enabled) {
        listenToOSThemeChanges();
      } else {
        stopListeningToOSThemeChanges();
      }
      applyColorScheme();
    },
    { immediate: true }
  );

  return {
    isDarkModeEnabled,
    setAppearance,
  };
};

export default useColorScheme;
